'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, Button, FormField, FormHelp, Heading, Input, Label, Modal, Select, Text } from '@zillow/constellation-10';
import { useCreateSavedSearch } from '@/src/lib/hooks/query/useSavedSearches';
import { useCurrentUser } from '@/src/lib/hooks/query/useUserQuery';
import {
  buildSavedSearchPayload,
  type SavedSearchAreaBounds,
  type SavedSearchFrequency,
} from '@/src/lib/savedSearches';
import { useFilters } from '@/src/lib/filters';
import {
  getSavedSearchNameError,
  SAVED_SEARCH_FREQUENCY_LABEL_KEYS,
  SAVED_SEARCH_FREQUENCY_OPTIONS,
} from './saveSearchFormUtils';

type SaveSearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  areaId: string;
  resourceId: string;
  searchSlug: string;
  locationText: string;
  areaBounds: SavedSearchAreaBounds;
};

export function SaveSearchModal({
  isOpen,
  onClose,
  onSaved,
  areaId,
  resourceId,
  searchSlug,
  locationText,
  areaBounds,
}: SaveSearchModalProps) {
  const t = useTranslations('saveSearchModal');
  const { filters } = useFilters();
  const { data: currentUser } = useCurrentUser();
  const createSavedSearch = useCreateSavedSearch();

  const [name, setName] = useState(locationText);
  const [frequency, setFrequency] = useState<SavedSearchFrequency>('instant');
  const [nameError, setNameError] = useState<string | undefined>();
  const wasOpenRef = useRef(false);

  const defaultEmail = currentUser?.data?.email ?? '';
  const hasResolvedIdentity = currentUser !== undefined;
  const canSubmit = hasResolvedIdentity && defaultEmail.trim().length > 0;

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    setName(locationText);
    setFrequency('instant');
    setNameError(undefined);
    createSavedSearch.reset();
  }, [createSavedSearch, isOpen, locationText]);

  const mutationError = useMemo(() => {
    const message = createSavedSearch.error instanceof Error ? createSavedSearch.error.message : '';
    return message || undefined;
  }, [createSavedSearch.error]);

  const handleSubmit = () => {
    if (!canSubmit) return;

    const trimmedName = name.trim();
    const trimmedEmail = defaultEmail.trim();
    const nextNameError = getSavedSearchNameError(trimmedName, t);

    setNameError(nextNameError);

    if (nextNameError) return;

    createSavedSearch.mutate(
      buildSavedSearchPayload({
        name: trimmedName,
        email: trimmedEmail,
        frequency,
        resourceId,
        areaId,
        searchSlug,
        filters,
        areaBounds,
      }),
      {
        onSuccess: () => {
          onSaved();
          onClose();
        },
      },
    );
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()} size={{ base: 'fullScreen', lg: 'sm' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content data-testid="listing-search-save" aria-busy={!hasResolvedIdentity}>
            <Modal.Header>
              <Heading level={2}>{t('title')}</Heading>
            </Modal.Header>
            <Modal.Body>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
                <Text textStyle="body-sm" color="text.subtle">
                  {t('subtitle')}
                </Text>

                <FormField
                  label={<Label>{t('nameLabel')}</Label>}
                  required
                  error={!!nameError}
                  description={nameError ? <FormHelp>{nameError}</FormHelp> : undefined}
                  control={
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={locationText}
                      size="md"
                    />
                  }
                />

                <FormField
                  label={<Label>{t('frequencyLegend')}</Label>}
                  control={
                    <Select
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value as SavedSearchFrequency)}
                      size="md"
                    >
                      {SAVED_SEARCH_FREQUENCY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {t(SAVED_SEARCH_FREQUENCY_LABEL_KEYS[option])}
                        </option>
                      ))}
                    </Select>
                  }
                />

                {mutationError && (
                  <Text textStyle="body-sm" color="text.subtle">
                    {mutationError}
                  </Text>
                )}
              </Box>
            </Modal.Body>
            <Modal.Footer>
              <Button
                tone="brand"
                emphasis="filled"
                size="md"
                fluid
                onClick={handleSubmit}
                disabled={!canSubmit || createSavedSearch.isPending}
              >
                {createSavedSearch.isPending ? t('saving') : t('submit')}
              </Button>
            </Modal.Footer>
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
